import {WindowSizeConsumer} from '@albert-io/atomic/helpers/WindowSizeProvider/WindowSizeProvider.react';
import Card from '@albert-io/atomic/atoms/Card/Card.react';
import cx from 'classnames';
import type {KeyboardEvent} from 'react';
import React, {useEffect, useRef} from 'react';
import {Popper} from 'react-popper';
import {useMenu} from './MenuContext.react';

// First match wins in document order, so a leading search input is focused for
// search-style menus; otherwise the first interactive item.
const FOCUSABLE_SELECTOR =
  'input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [role="menuitem"]:not([tabindex^="-"]), button:not([disabled]):not([tabindex^="-"]), a[href], [tabindex]:not([tabindex^="-"])';

const modifiers = {
  preventOverflow: {
    padding: 64,
    boundariesElement: 'viewport' as const
  },
  flip: {
    padding: 16
  },
  setMobile: {
    enabled: false
  }
};

interface MenuContentProps extends PropsWithChildrenRequired, PropsWithClassNameOptional {}

const MenuContent = ({children, className = ''}: MenuContentProps) => {
  const {isOpen, close, position, positionFixed, popperProps} = useMenu();
  const menuRef = useRef<HTMLDivElement>(null);
  // The element focused when the menu opened (the trigger), so keyboard
  // dismissal can return focus to it per the ARIA menu-button pattern.
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    previouslyFocusedRef.current = (document.activeElement as HTMLElement | null) ?? null;
    menuRef.current?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR)?.focus();
  }, [isOpen]);

  const closeAndRestoreFocus = () => {
    previouslyFocusedRef.current?.focus();
    close();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (!menuRef.current) return;

    const menuItems = Array.from(
      menuRef.current.querySelectorAll('[role="menuitem"]:not([tabindex^="-"])')
    ) as HTMLElement[];

    const currentIndex = menuItems.findIndex((item) => item === document.activeElement);

    switch (e.key) {
      case 'ArrowDown': {
        e.preventDefault();
        const nextIndex = (currentIndex + 1) % menuItems.length;
        menuItems[nextIndex]?.focus();
        break;
      }
      case 'ArrowUp': {
        e.preventDefault();
        const prevIndex = currentIndex <= 0 ? menuItems.length - 1 : currentIndex - 1;
        menuItems[prevIndex]?.focus();
        break;
      }
      // Tab must be intercepted or it would step through items while the menu
      // stays open. Both leave the menu and restore focus to the trigger;
      // this covers Shift+Tab (same key).
      case 'Tab':
      case 'Escape': {
        e.preventDefault();
        closeAndRestoreFocus();
        break;
      }
      default:
        break;
    }
  };

  if (!isOpen) return null;

  return (
    <WindowSizeConsumer>
      {(context) => {
        if (!context) return null;

        const {viewportWidth, breakpoints} = context;
        const isMobile = viewportWidth <= breakpoints.s;

        // Update mobile modifier
        modifiers.setMobile.enabled = isMobile;

        return (
          <Popper
            className={cx('o-menu-content__wrapper', className, {
              'o-menu-content__wrapper--show': isOpen
            })}
            modifiers={modifiers}
            placement={position}
            positionFixed={positionFixed}
            {...popperProps}
          >
            <Card
              paddingLevel={1}
              // @ts-expect-error role is a valid prop on Card since it's a div wrapper
              role='menu'
              shadow='close'
              className='o-menu-content'
              onKeyDown={handleKeyDown}
              ref={menuRef}
            >
              {children}
            </Card>
          </Popper>
        );
      }}
    </WindowSizeConsumer>
  );
};

export default MenuContent;
